import { caseDbApiEndpoint } from '../utils/apiOrigin';
import { withCsrfHeaders } from '../utils/csrf';

const DEFAULT_ADMIN_PATH = process.env.REACT_APP_PUBLIC_FORMS_ADMIN_PATH || 'public-forms-admin.php';

export const publicFormsAdminEndpoint = () => caseDbApiEndpoint(DEFAULT_ADMIN_PATH);

const readPayload = async (response) => {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    const error = new Error(
      payload?.message
      || payload?.error
      || (response.status === 404 || response.status === 503
        ? 'Public forms are not configured for this environment yet.'
        : 'CaseDB could not complete the public form request.'),
    );
    error.status = response.status;
    error.code = payload?.code || payload?.error_code || '';
    error.payload = payload;
    throw error;
  }
  return payload?.data && typeof payload.data === 'object' ? { ...payload, ...payload.data } : payload;
};

const adminGet = async (action, params = {}, options = {}) => {
  const endpoint = options.endpoint || publicFormsAdminEndpoint();
  const url = new URL(endpoint, window.location.href);
  url.searchParams.set('action', action);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== '' && value !== null && value !== undefined) url.searchParams.set(key, String(value));
  });
  const response = await fetch(url.toString(), {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
    signal: options.signal,
  });
  return readPayload(response);
};

const adminPost = async (action, body = {}, options = {}) => {
  const endpoint = options.endpoint || publicFormsAdminEndpoint();
  const response = await fetch(endpoint, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: withCsrfHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' }, 'POST'),
    body: JSON.stringify({ action, ...body }),
    signal: options.signal,
  });
  return readPayload(response);
};

export const publicFormsApi = Object.freeze({
  list: (options) => adminGet('list', {}, options),
  catalog: (caseTypeId, options) => adminGet('catalog', { case_type_id: caseTypeId }, options),
  get: (formId, options) => adminGet('get', { form_id: formId }, options),
  links: (formId, options = {}) => adminGet('links', {
    form_id: formId,
    page: options.page,
    per_page: options.perPage,
  }, options),
  create: (form, options) => adminPost('create', form, options),
  update: (formId, form, options) => adminPost('update', { ...form, form_id: formId }, options),
  publish: (formId, options) => adminPost('publish', { form_id: formId }, options),
  issueLink: (formId, linkOptions, options) => adminPost('issue_link', { form_id: formId, ...linkOptions }, options),
  revokeLink: (linkId, options) => adminPost('revoke_link', { link_id: linkId }, options),
  revoke: (formId, options) => adminPost('revoke', { form_id: formId }, options),
  matterRequestContext: (request, options) => adminGet('matter_request_context', request, options),
  sendMatterRequest: (request, options) => adminPost('send_matter_request', request, options),
  previewSmsBatch: (request, options) => adminPost('preview_sms_batch', request, options),
  confirmSmsBatch: (request, options) => adminPost('confirm_sms_batch', request, options),
  smsBatchStatus: (request, options) => adminGet('sms_batch_status', request, options),
  smsBatches: (formId, options) => adminGet('list_sms_batches', { form_id: formId }, options),
  submissions: (formId, options = {}) => adminGet('submissions', {
    form_id: formId,
    link_id: options.linkId,
    page: options.page,
    per_page: options.perPage,
  }, options),
  submission: (formId, submissionId, options) => adminGet('submission', {
    form_id: formId,
    submission_id: submissionId,
  }, options),
});

export const publicFormFeatureError = (error) => {
  if (error?.status === 404 || error?.status === 503 || error?.code === 'feature_disabled') {
    return 'Public forms are installed but not enabled in this environment. No public links can be used until an administrator finishes the secure runtime configuration.';
  }
  return error instanceof Error ? error.message : 'Public forms are unavailable.';
};
